import { useState } from 'react';
import { makeStyles } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';

const useStyles = makeStyles({
    name: 'UAEPassButton'
})((theme) => ({
    button: {
        height: '52px',
        width: 'auto',
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',

        '&:hover:not(:disabled)': {
            opacity: 0.95,
        },

        '&:focus:not(:focus-visible)': {
            outline: 'none',
        },

        '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
            borderRadius: '4px',
        },

        '&:disabled': {
            cursor: 'not-allowed',
        },

        [theme.breakpoints.down('md')]: {
            height: '44px',
        },
    },

    buttonImage: {
        height: '52px',
        width: 'auto',
        display: 'block',
        pointerEvents: 'none',
        [theme.breakpoints.down('md')]: {
            height: '44px',
        },
    },
}));

const getButtonImageSrc = (variant, language, state, purpose) => {
    const basePath = '/assets/uaepass/buttons';
    const label = purpose === 'continue' ? `${variant}-continue` : variant;
    if (state === 'active') {
        return `${basePath}/${variant}/uaepass-button-${label}-${language}.png`;
    }
    return `${basePath}/${variant}/uaepass-button-${label}-${language}-${state}.png`;
};

const UAEPassButton = ({
    variant = 'black',
    purpose = 'signin',
    language,
    onClick,
    href,
    disabled = false,
    className,
    ...rest
}) => {
    const { classes } = useStyles();
    const intl = useIntl();
    const [isFocused, setIsFocused] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const buttonLanguage = language || (intl.locale === 'ar' ? 'ar' : 'en');
    const altText = intl.formatMessage({
        id: purpose === 'continue' ? 'uaePassContinue' : 'uaePassSignIn',
        defaultMessage: purpose === 'continue' ? 'Continue with UAEPASS' : 'Sign in with UAEPASS'
    });

    const buttonState = disabled ? 'disabled' : isPressed ? 'pressed' : isFocused ? 'focus' : 'active';
    const imageSrc = getButtonImageSrc(variant, buttonLanguage, buttonState, purpose);

    const handleClick = (e) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        if (href) {
            window.location.href = href;
            return;
        }
        if (onClick) {
            onClick(e);
        }
    };

    const handleKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
        }
    };

    const combinedClassName = `${classes.button} ${className || ''}`.trim();

    return (
        <button
            type="button"
            className={combinedClassName}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => { setIsFocused(false); setIsPressed(false); }}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => { setIsPressed(false); setIsFocused(false); }}
            onMouseLeave={() => setIsPressed(false)}
            onTouchStart={() => setIsPressed(true)}
            onTouchEnd={() => { setIsPressed(false); setIsFocused(false); }}
            disabled={disabled}
            aria-label={altText}
            tabIndex={disabled ? -1 : 0}
            {...rest}
        >
            <img
                src={imageSrc}
                alt=""
                className={classes.buttonImage}
                draggable="false"
                loading="eager"
            />
        </button>
    );
};

export default UAEPassButton;
